import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Switch,
    Typography
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import CastleRoundedIcon from '@mui/icons-material/CastleRounded'
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'

import AutomaticFeastSettingsPanel from './AutomaticFeastSettingsPanel'
import AllianceCallAlertPanel from './AllianceCallAlertPanel'
import AllianceRiftPortalPanel from './AllianceRiftPortalPanel'
import GrandTournamentPanel from './GrandTournamentPanel'
import BerriGreenSettingsPanel from './BerriGreenSettingsPanel'
import BotSettingsPanel, { CoreLocationsPanel } from './BotSettingsPanel'
import CallSetupPanel from './CallSetupPanel'
import CampBotSettingsPanel from './CampBotSettingsPanel'
import CommandersCastellansCraftingPanel from './CommandersCastellansCraftingPanel'
import CommanderAllocationPanel, { getCommanderOptions } from './CommanderAllocationPanel'
import AttackSetupPanel from './AttackSetupPanel'
import ArmorerSettingsPanel from './ArmorerSettingsPanel'
import BerriWorldSettingsPanel from './BerriWorldSettingsPanel'
import BthInfluenceAttackPanel from './BthInfluenceAttackPanel'
import BthInfluenceHunterPanel from './BthInfluenceHunterPanel'
import BthMapAttackSettingsPanel from './BthMapAttackSettingsPanel'
import BthTaxCollectorSettingsPanel from './BthTaxCollectorSettingsPanel'
import BotTimetableDialog from './BotTimetableDialog'
import CastleDefenseSettingsPanel from './CastleDefenseSettingsPanel'
import DiscordNotificationsPanel from './DiscordNotificationsPanel'
import EquipmentSellerSettingsPanel from './EquipmentSellerSettingsPanel'
import FoodCheckSettingsPanel from './FoodCheckSettingsPanel'
import ForeignCastlesBotSettingsPanel from './ForeignCastlesBotSettingsPanel'
import FortressBotSettingsPanel from './FortressBotSettingsPanel'
import FunctionSelectionPanel from './FunctionSelectionPanel'
import HospitalHealerSettingsPanel from './HospitalHealerSettingsPanel'
import KhanAttackConfigurator from './KhanAttackConfigurator'
import LuckyWheelSettingsPanel from './LuckyWheelSettingsPanel'
import MasterBlacksmithSettingsPanel from './MasterBlacksmithSettingsPanel'
import NativeVillageSettingsPanel from './NativeVillageSettingsPanel'
import PluginManager, { getPluginManagerVisiblePlugins } from './PluginManager'
import ProduceComponentsSettingsPanel from './ProduceComponentsSettingsPanel'
import RecruitBotSettingsPanel from './RecruitBotSettingsPanel'
import ServerSettingsPanel from './ServerSettingsPanel'
import SpeedSettingsPanel from './SpeedSettingsPanel'
import SkipsTradeSettingsPanel from './SkipsTradeSettingsPanel'
import StormBotSettingsPanel from './StormBotSettingsPanel'
import TechnicusSettingsPanel from './TechnicusSettingsPanel'
import TowerBotSettingsPanel from './TowerBotSettingsPanel'
import ToolBuildSettingsPanel from './ToolBuildSettingsPanel'
import UserCredentialsPanel from './UserCredentialsPanel'
import MainLayout from '../layout/MainLayout'
import LiveDecoPanel from './LiveDecoPanel'
import { ActionType, ErrorType } from '../types.js'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { normalizeBotSettings } from '../utils/botSettings'
import { normalizeLogBuffer } from '../utils/liveLogs'
import { countActiveTimetableBlocks, normalizeTimetable } from '../utils/timetable'

const SECTION_IDS = Object.freeze({
    bot: 'bot-settings',
    allianceRiftPortal: 'alliance-rift-portal',
    grandTournament: 'grand-tournament',
    liveCastle: 'live-castle',
    liveDeco: 'live-deco',
    credentials: 'user-credentials',
    speed: 'speed-settings',
    crafting: 'commanders-castellans-crafting',
    luckyWheel: 'lucky-wheel',
    functions: 'function-selection',
    attack: 'group:attack',
    commander: 'group:commander',
    call: 'group:call',
    allianceCall: 'alliance-call-alert',
    notifications: 'group:notifications'
})

const ATTACK_PLUGIN_NAMES = new Set([
    'Attack',
    'Attack Barrons (Great Empire)',
    'Attack Barrons (Fire Peaks)',
    'Attack Barrons (Glacier)',
    'Attack Barrons (Burning Sands)',
    'Attack Barrons (Outer Realms)',
    'Attack Beri Camps',
    'Attack Fortress (Fire Peaks)',
    'Attack Fortress (Everwinter Glacier)',
    'Attack Fortress (Burning Sands)',
    'Attack Aqua Forts',
    'Khan Attack',
    'Attack Nomad Camps',
    'Attack Samurai Camps',
    'Attack Berimond Kingdom'
])

const NOTIFICATION_PLUGIN_NAMES = new Set([
    'Player Alert',
    'Alliance Player Alert'
])

const AUTO_SPEED_PLUGIN_KEY = 'autospeed'
const ALLIANCE_RIFT_PORTAL_PLUGIN_KEY = 'alliance_rift_portal'
const ALLIANCE_RIFT_COMMANDER_PLUGIN = Object.freeze({
    key: ALLIANCE_RIFT_PORTAL_PLUGIN_KEY,
    name: 'Rift',
    description: 'Commander allocation for Alliance RIFT Portal attacks',
    pluginOptions: [{
        type: 'Text',
        key: 'commanderWhiteList',
        label: 'Commander White List',
        emptyMeansNone: true
    }]
})
const CALL_SETUP_CORE_PLUGIN_KEYS = new Set(['alert', 'twilio_alert'])
const ALLIANCE_CALL_CORE_PLUGIN_KEYS = new Set(['twilio_alert_alliance'])
const CORE_SETTINGS_PLUGIN_KEYS = new Set([...CALL_SETUP_CORE_PLUGIN_KEYS, ...ALLIANCE_CALL_CORE_PLUGIN_KEYS])
const PRODUCE_COMPONENTS_SECTION_KEY = 'produce-components'
const PRODUCE_COMPONENTS_PLUGIN_KEYS = new Set(['auto_craft_components', 'auto_craft_refinery'])
const TOWER_BOT_SECTION_KEY = 'tower-bot'
const TOWER_BOT_PLUGIN_KEYS = new Set(['attackbarronsempire', 'attackbarronsglacier', 'attackbarronssands', 'attackbarronsfire'])
const FORTRESS_BOT_SECTION_KEY = 'fortress-bot'
const FORTRESS_BOT_PLUGIN_KEYS = new Set(['attackfortresses', 'attackfortresses_glacier', 'attackfortresses_sand'])
const NOMADS_BOT_SECTION_KEY = 'camp-bot-nomads'
const SAMURAI_BOT_SECTION_KEY = 'camp-bot-samurai'
const CAMP_BOT_RUNTIME_PLUGIN_KEYS = new Set(['attack', 'attacknomads', 'attacksams'])
const STORM_BOT_PRIMARY_PLUGIN_KEY = 'attackforts'
const STORM_BOT_HIDDEN_PLUGIN_KEYS = new Set(['islandcargo'])
const BTH_INFLUENCE_ATTACK_PLUGIN_KEY = 'bth_influence_attack'
const BTH_MAP_ATTACK_PLUGIN_KEY = 'bth_map_attack'
const BTH_TAX_COLLECTOR_PLUGIN_KEY = 'bth_tax_collector'

const MODULE_SECTION_META = Object.freeze({
    [TOWER_BOT_SECTION_KEY]: {
        label: 'Tower Bot Settings',
        description: 'Configure tower attack parameters and kingdom settings',
        icon: AccountBalanceRoundedIcon
    },
    [PRODUCE_COMPONENTS_SECTION_KEY]: {
        label: 'Produce Components Settings',
        description: 'Configure recipe-list based production for Refinery, Toolsmith, DragonHoard, and DragonBreathForge',
        icon: Inventory2RoundedIcon
    },
    [FORTRESS_BOT_SECTION_KEY]: {
        label: 'Fortress Bot Settings',
        description: 'Configure fortress attack parameters and kingdom settings',
        icon: ShieldRoundedIcon
    },
    [NOMADS_BOT_SECTION_KEY]: {
        label: 'Nomads Bot Settings',
        description: 'Configure only Nomad camp attacks, targets, templates, and skips',
        icon: GpsFixedRoundedIcon
    },
    [SAMURAI_BOT_SECTION_KEY]: {
        label: 'Samurai Bot Settings',
        description: 'Configure only Samurai Camps and Classic Daimyo',
        icon: GpsFixedRoundedIcon
    },
    [AUTO_SPEED_PLUGIN_KEY]: {
        label: 'Speed Settings',
        description: 'Activate the Global Effect for a Speed Boost automatically',
        icon: SpeedRoundedIcon
    },
    flee: {
        label: 'Castle Defense Settings',
        description: 'Per-location thresholds, Main/Outposts, and Kingdoms',
        icon: ShieldRoundedIcon
    },
    attackaliens: {
        label: 'Foreign Castles Bot Settings',
        description: 'Scout and attack Foreign Invader and Bloodcrow camps',
        icon: AccountBalanceRoundedIcon
    },
    attackforts: {
        label: 'Storm Bot Settings',
        description: 'Configure storm attack parameters, Aqua shop purchases, and supply management',
        icon: GpsFixedRoundedIcon
    },
    attackberi: {
        label: 'Berri Green Settings',
        description: 'Configure green camp rotation and timing',
        icon: StarBorderRoundedIcon
    },
    berikingdom: {
        label: 'Berri World Settings',
        description: 'Configure Berimond attacks, troop management, and player attack options',
        icon: StarBorderRoundedIcon
    },
    barracks_modified: {
        label: 'Recruit Bot Settings',
        description: 'Configure automatic troop recruitment for the selected game mode',
        icon: GroupsRoundedIcon
    },
    feast: {
        label: 'Automatic Feast Settings',
        description: 'Configure automatic feast management for all kingdoms',
        icon: RestaurantRoundedIcon
    },
    hospital: {
        label: 'Hospital Healer Settings',
        description: 'Configure military hospital automation behavior per castle.',
        icon: CastleRoundedIcon
    },
    auto_recruit_tools: {
        label: 'Tool Build Settings',
        description: 'Configure automatic tool building queues for siege and defense workshops.',
        icon: BuildRoundedIcon
    },
    equipment_seller: {
        label: 'Equipment Seller Settings',
        description: 'Configure automatic equipment and gem selling rules',
        icon: Inventory2RoundedIcon
    },
    tax_collector: {
        label: 'Automatic Tax Collector',
        description: 'Collect taxes and start the next period in the normal game',
        icon: AccountBalanceRoundedIcon
    },
    bth_tax_collector: {
        label: 'BTH Automatic Tax Collector',
        description: 'Collect the BTH tax sack and start the next period',
        icon: AccountBalanceRoundedIcon
    },
    bth_map_attack: {
        label: 'BTH Map Attack',
        description: 'Complete resource gates and attack ready treasure-map nodes',
        icon: AccountBalanceRoundedIcon
    },
    attackkhan: {
        label: 'Khan Bot Settings',
        description: 'Configure Khan camp farming parameters',
        icon: StarBorderRoundedIcon
    },
    skips_trade: {
        label: 'Skips Trade Settings',
        description: 'Configure automated Time Skip Exchange trades and thresholds',
        icon: SwapHorizRoundedIcon
    },
    meadrep: {
        label: 'Food Check Settings',
        description: 'Configure automatic food monitoring and management',
        icon: RestaurantRoundedIcon
    },
    nativevillage: {
        label: 'Native Village Settings',
        description: 'Configure Native Village shop purchases per kingdom',
        icon: CastleRoundedIcon
    },
    armorer: {
        label: 'Armorer Settings',
        description: 'Configure Armorer package purchases and prices',
        icon: StorefrontRoundedIcon
    },
    grabtools: {
        label: 'Master Blacksmith',
        description: 'Configure live Master Blacksmith offers, costs, and purchases',
        icon: BuildRoundedIcon
    },
    technicus: {
        label: 'Technicus Settings',
        description: 'Configure inventory, commander, and castellan equipment upgrades',
        icon: BuildRoundedIcon
    },
    [BTH_INFLUENCE_ATTACK_PLUGIN_KEY]: {
        label: 'BTH Influence Attacks',
        description: 'Scan players and capitals and send BTH attacks ranked by influence',
        icon: GpsFixedRoundedIcon
    }
})

export const getRoutedModuleActiveState = userPlugins => ({
    nomadsBot: Boolean(userPlugins?.attacknomads?.state),
    samuraiBot: Boolean(userPlugins?.attacksams?.state),
    speedSettings: Boolean(userPlugins?.[AUTO_SPEED_PLUGIN_KEY]?.state || userPlugins?.[AUTO_SPEED_PLUGIN_KEY]?.enabled)
})
const STATIC_MODULE_SECTION_KEYS = Object.keys(MODULE_SECTION_META)

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const normalizeExternalEventType = value => {
    const normalizedValue = String(value ?? '').trim().toLowerCase()

    if (!normalizedValue)
        return ''

    if (['auto', 'legacy', 'or_bth', 'or/bth'].includes(normalizedValue))
        return 'auto'

    if (['outer_realms', 'outer realms', 'outerrealms', 'or'].includes(normalizedValue))
        return 'outer_realms'

    if (['battle_for_the_throne', 'battle for the throne', 'battleforthethrone', 'bth'].includes(normalizedValue))
        return 'battle_for_the_throne'

    return ''
}

export const isBthInfluenceModuleVisible = value => normalizeExternalEventType(value) === 'battle_for_the_throne'

const formatDateTime = value => {
    const timestamp = Number(value)

    if (!Number.isFinite(timestamp) || timestamp <= 0)
        return 'No recent activity'

    return new Date(timestamp).toLocaleString()
}

const hasAnyPluginStateEnabled = (userPlugins, pluginKeys) =>
    [...pluginKeys].some(pluginKey => Boolean(userPlugins?.[pluginKey]?.state))

const downloadTextFile = (filename, content, mimeType = 'text/plain;charset=utf-8') => {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')

    anchor.href = url
    anchor.download = filename
    anchor.click()

    window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

const readResponseText = async response => {
    if (!response.ok)
        throw new Error(`HTTP ${response.status}`)

    return response.text()
}

const readXmlNodeText = (node, name) => node.getElementsByTagName(name)?.[0]?.textContent ?? ''

const getServerCodeBase = instanceLocaId => {
    const normalizedValue = String(instanceLocaId ?? '')
        .replace(/^generic_(country|language)_/i, '')
        .replace(/^generic_/i, '')
        .trim()
        .toUpperCase()

    if (normalizedValue === 'INTERNATIONAL')
        return 'INT'

    if (normalizedValue === 'WORLD')
        return 'WORLD'

    return normalizedValue || 'SRV'
}

const createServerOption = (node, lang, platform) => {
    const id = Number(node.getAttribute('value'))

    if (!Number.isFinite(id))
        return null

    const instanceLocaId = readXmlNodeText(node, 'instanceLocaId')
    const instanceName = String(readXmlNodeText(node, 'instanceName')).trim()
    const localizedName = String(lang?.[instanceLocaId] ?? instanceLocaId ?? '').trim()
    const name = [localizedName, instanceName].filter(Boolean).join(' ').trim() || `Server ${id}`
    const codeBase = getServerCodeBase(instanceLocaId)
    const code = platform === 'e4k' ? `E4K_${codeBase}${instanceName}` : `${codeBase}${instanceName}`

    return {
        id,
        platform,
        networkLabel: platform === 'e4k' ? 'E4K' : 'Empire',
        name,
        code,
        label: `${name} (${code})`,
        serverHost: readXmlNodeText(node, 'server'),
        instanceLocaId
    }
}

const parseServerCatalog = (xmlText, lang, platform) => {
    const xml = new DOMParser().parseFromString(xmlText, 'text/xml')
    const nodes = Array.from(xml.getElementsByTagName('instance'))

    return nodes
        .map(node => createServerOption(node, lang, platform))
        .filter(Boolean)
}

const loadServerInstances = async () => {
    try {
        const [langText, empireXmlText, e4kXmlText] = await Promise.all([
            fetch(`${window.location.origin}/lang.json`).then(readResponseText),
            fetch(`${window.location.origin}/1.xml`).then(readResponseText),
            fetch(`${window.location.origin}/72.xml`).then(readResponseText)
        ])

        const lang = JSON.parse(langText)

        return [
            ...parseServerCatalog(empireXmlText, lang, 'gge'),
            ...parseServerCatalog(e4kXmlText, lang, 'e4k')
        ]
    }
    catch (error) {
        console.warn('Unable to load server catalog', error)
        return []
    }
}

const isPluginActive = (plugin, userPlugins) => Boolean(userPlugins?.[plugin?.key]?.state)

export const isCommanderPlugin = plugin => getCommanderOptions(plugin).length > 0

export const getRestrictedCoreSectionVisibility = (isAdmin, selectedUser) => ({
    allianceRiftPortal: Boolean(isAdmin || selectedUser?.allianceRiftPortalApproved),
    grandTournament: Boolean(isAdmin || selectedUser?.grandTournamentApproved)
})

export const shouldRequestGrandTournamentApproval = (isAdmin, nextEnabled) =>
    Boolean(isAdmin && nextEnabled)

const isCallPlugin = plugin => {
    const pluginName = String(plugin?.name ?? '').toLowerCase()
    if (pluginName.includes('call'))
        return true

    return Array.isArray(plugin?.pluginOptions) &&
        plugin.pluginOptions.some(option => {
            const optionKey = String(option?.key ?? '').toLowerCase()
            const optionLabel = String(option?.label ?? '').toLowerCase()
            return optionKey.includes('call') || optionLabel.includes('call')
        })
}

const getDynamicSidebarSections = (plugins, userPlugins) => {
    const allPlugins = Array.isArray(plugins) ? plugins : []

    const activePlugins = allPlugins.filter(plugin => isPluginActive(plugin, userPlugins))

    return [
        {
            id: SECTION_IDS.attack,
            label: 'Attack Setup',
            icon: <GpsFixedRoundedIcon />,
            plugins: activePlugins.filter(plugin => ATTACK_PLUGIN_NAMES.has(plugin.name)),
            description: 'Configure active attack modules'
        },
        {
            id: SECTION_IDS.commander,
            label: 'Commander Allocation',
            icon: <GroupsRoundedIcon />,
            plugins: allPlugins.filter(plugin => isCommanderPlugin(plugin)),
            description: 'Assign commanders to modules and define the fallback policy.'
        },
        {
            id: SECTION_IDS.call,
            label: 'Call Setup',
            icon: <PhoneInTalkRoundedIcon />,
            plugins: activePlugins.filter(plugin => isCallPlugin(plugin)),
            description: 'Prepare call-related modules and sharing rules'
        },
        {
            id: SECTION_IDS.notifications,
            label: 'Discord Notifications',
            icon: <NotificationsActiveRoundedIcon />,
            plugins: activePlugins.filter(plugin => NOTIFICATION_PLUGIN_NAMES.has(plugin.name)),
            description: 'Review alert and notification modules'
        }
    ]
}

function SidebarSection({ active, icon, label, onClick }) {
    return (
        <ListItemButton
            onClick={onClick}
            sx={{
                borderRadius: 2.6,
                mb: 0.45,
                backgroundColor: active ? 'rgba(106, 127, 161, 0.38)' : 'transparent',
                borderLeft: active ? '2px solid #4380ff' : '2px solid transparent',
                '&:hover': {
                    backgroundColor: active ? 'rgba(106, 127, 161, 0.45)' : 'rgba(255,255,255,0.04)'
                }
            }}
        >
            <ListItemIcon sx={{ minWidth: 34, color: active ? 'text.primary' : 'text.secondary' }}>
                {icon}
            </ListItemIcon>
            <ListItemText
                primary={label}
                primaryTypographyProps={{
                    fontWeight: active ? 700 : 600,
                    fontSize: '0.98rem'
                }}
            />
        </ListItemButton>
    )
}

function OverviewStat({ label, value, caption }) {
    return (
        <Box
            sx={{
                p: 1.6,
                borderRadius: 2.6,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)'
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.45, fontSize: '1rem' }}>
                {value}
            </Typography>
            {caption && (
                <Typography variant="body2" sx={{ mt: 0.4, color: 'text.secondary' }}>
                    {caption}
                </Typography>
            )}
        </Box>
    )
}

function ToolbarButton({ children, startIcon, onClick, variant = 'outlined', color = 'inherit', disabled = false }) {
    return (
        <Button
            variant={variant}
            color={color}
            startIcon={startIcon}
            disabled={disabled}
            onClick={onClick}
            sx={{
                minWidth: { xs: '100%', sm: 'auto' },
                px: 2,
                py: 1.1,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                ...(variant === 'contained' ? {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none'
                    }
                } : {})
            }}
        >
            {children}
        </Button>
    )
}

export default function SettingsDrawer(props) {
    const {
        selectedUser,
        selectedUserStatus,
        plugins,
        channels,
        ws,
        onClose,
        onBotCreated,
        onStartLiveOps,
        onStopLiveOps,
        logs,
        liveOpsActive = false,
        appLanguage = 'en',
        accountInfo = null,
        currentView = 'dashboard',
        isAdmin = false,
        onAppLanguageChange,
        onViewChange
    } = props

    const editorRef = React.useRef(null)
    const creationRef = React.useRef(null)
    const createdBotIdRef = React.useRef(null)
    const [createdBotId, setCreatedBotId] = React.useState(null)
    React.useEffect(() => {
        creationRef.current = null
        createdBotIdRef.current = null
        setCreatedBotId(null)
    }, [selectedUser])
    React.useEffect(() => {
        const receiveCreation = event => {
            let message
            try { message = JSON.parse(event.data) } catch { return }
            const pending = creationRef.current
            if (!pending || message[1] !== ActionType.AddUser || message[2]?.creationRequestId !== pending.requestId)
                return
            creationRef.current = null
            if (message[0] !== ErrorType.Success || !message[2]?.id) {
                setFeedback({ severity: 'error', message: 'Bot creation failed. Please try again.' })
                return
            }
            const id = message[2].id
            createdBotIdRef.current = id
            setCreatedBotId(id)
            // Apply the latest configuration, including a requested Start, to the confirmed bot.
            ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, { ...pending.payload, id }]))
            onBotCreated?.({ ...pending.payload, id })
        }
        const retryCreation = () => {
            const pending = creationRef.current
            if (pending)
                ws.send(JSON.stringify([ErrorType.Success, ActionType.AddUser, { ...pending.payload, creationRequestId: pending.requestId }]))
        }
        ws.addEventListener('message', receiveCreation)
        ws.addEventListener('open', retryCreation)
        return () => {
            ws.removeEventListener('message', receiveCreation)
            ws.removeEventListener('open', retryCreation)
        }
    }, [ws, onBotCreated])
    const sendBotConfiguration = React.useCallback(payload => {
        const id = selectedUser?.id || createdBotIdRef.current
        if (id) {
            ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, { ...payload, id }]))
        } else if (creationRef.current) {
            creationRef.current.payload = payload
        } else {
            const requestId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
            creationRef.current = { requestId, payload }
            try {
                ws.send(JSON.stringify([ErrorType.Success, ActionType.AddUser, { ...payload, creationRequestId: requestId }]))
            } catch (error) {
                creationRef.current = null
                throw error
            }
        }
    }, [selectedUser?.id, ws])
    const liveOpsRef = React.useRef(null)
    const fileInputRef = React.useRef(null)
    const logViewportRef = React.useRef(null)
    const [activeSection, setActiveSection] = React.useState(SECTION_IDS.bot)
    const [name, setName] = React.useState(selectedUser?.name ?? '')
    const [pass, setPass] = React.useState(selectedUser?.pass ?? '')
    const [userPlugins, setUserPlugins] = React.useState(() => cloneValue(selectedUser?.plugins ?? {}))
    const [platform, setPlatform] = React.useState(selectedUser?.platform === 'e4k' ? 'e4k' : 'gge')
    const [server, setServer] = React.useState(selectedUser?.server ?? '')
    const [externalEventType, setExternalEventType] = React.useState(normalizeExternalEventType(selectedUser?.externalEventType))
    const [botSettings, setBotSettings] = React.useState(() => normalizeBotSettings(selectedUser?.botSettings))
    const [timetable, setTimetable] = React.useState(() => normalizeTimetable(selectedUser?.timetable))
    const [botRunning, setBotRunning] = React.useState(Boolean(selectedUser?.state))
    const [serverOptions, setServerOptions] = React.useState([])
    const [feedback, setFeedback] = React.useState(null)
    const [logsHidden, setLogsHidden] = React.useState(false)
    const [autoScroll, setAutoScroll] = React.useState(true)
    const [timetableOpen, setTimetableOpen] = React.useState(false)
    const showCallSection = Boolean(isAdmin || selectedUser?.callSetupApproved)
    const showAllianceCallSection = Boolean(isAdmin || selectedUser?.allianceCallApproved)
    const restrictedCoreSectionVisibility = getRestrictedCoreSectionVisibility(isAdmin, selectedUser)
    const showAllianceRiftPortalSection = restrictedCoreSectionVisibility.allianceRiftPortal
    const showGrandTournamentSection = restrictedCoreSectionVisibility.grandTournament

    const t = useAutoTranslate(appLanguage, [
        'Configure',
        'Manage your bot\'s configuration, templates, and settings',
        'Manage User Access',
        'Back',
        'Status',
        'Stopped',
        'Running',
        'Open Live Ops',
        'Start',
        'Stop',
        'Import',
        'Export',
        'Templates',
        'Save',
        'Configuration Editor',
        'Edit your bot\'s configuration',
        'Bot Settings',
        'Core bot identity, approval, and behavior details',
        'LIVE Castle',
        'Live castle overview for troops and tools',
        'Updated',
        'In castle',
        'troops',
        'tool types',
        'TROOPS',
        'TOOLS',
        'Defense setup',
        'WALL SETUP',
        'MOAT SETUP',
        'Left',
        'Middle',
        'Right',
        'Slot',
        'No wall or moat tools equipped.',
        'No troops or tools.',
        'User Credentials',
        'Login credentials for the game',
        'Server Settings',
        'Game server configuration',
        'Activate the Global Effect for a Speed Boost automatically',
        'Speed Settings',
        'Tune movement speed and timing helpers',
        'Commanders & Castellans Crafting',
        'Configure separate crafting priorities for commanders and castellans.',
        'Lucky Wheel',
        'Queue spin runs and inspect the latest recorded wheel rewards.',
        'Attack Setup',
        'Configure active attack modules',
        'Attack template selections for different bot types',
        'Function Selection',
        'Enable or disable bot functions',
        'Enable, disable, and fine-tune your installed modules',
        'Commander Allocation',
        'Adjust commander-related rules for enabled modules',
        'Call Setup',
        'Configure Player Alert for approved accounts',
        'Alliance Call Alert',
        'Configure Alliance Player Alert for approved accounts',
        'Discord Notifications',
        'Configure Discord webhook notifications',
        'Review alert and notification modules',
        'Basic bot behavior and timing settings',
        'Core Settings',
        'Modules',
        'Bot Name',
        'Password',
        'Platform',
        'Server',
        'Event Mode',
        'Standard',
        'Outer Realms / BtH',
        'Outer Realms',
        'Battle For The Throne',
        'Account UUID',
        'Owner Username',
        'Approval',
        'Approved',
        'Approval Required',
        'Last Seen',
        'Live Ops',
        'Timetable',
        'Define when the bot should run automatically.',
        'Open Timetable',
        'Timetable disabled',
        'Timetable enabled',
        'Active time slots',
        'Bot Monitoring',
        'Log Polling',
        'Active',
        'Inactive',
        'Click to start',
        'Start Polling',
        'Bot is Stopped',
        'Start your bot to begin monitoring and log polling',
        'Bot is Running',
        'Live monitoring is available for this bot right now',
        'Live Ops Ready',
        'Start polling to view logs',
        'Bot Logs',
        'Pause',
        'Clear',
        'Auto-scroll',
        'Updates',
        'No logs captured yet.',
        'Please enter a bot name before saving.',
        'Please set a password for new bots before saving.',
        'Password hidden for bots owned by another account.',
        'Configuration saved locally and sent to the server.',
        'Crafting start request sent.',
        'Unable to read that file. Please import a JSON export from this page.',
        'Imported configuration draft. Review it and press Save.',
        'Unable to start live polling right now.',
        'No recent activity',
        'Polling Active',
        'Polling Inactive',
        'This section is ready.',
        'Send the design and required functions for this section and I will build it here.'
    ])

    React.useEffect(() => {
        setActiveSection(Boolean(selectedUser?.state) ? SECTION_IDS.liveCastle : SECTION_IDS.bot)
        setName(selectedUser?.name ?? '')
        setPass(selectedUser?.pass ?? '')
        setUserPlugins(cloneValue(selectedUser?.plugins ?? {}))
        setPlatform(selectedUser?.platform === 'e4k' ? 'e4k' : 'gge')
        setServer(selectedUser?.server ?? '')
        setExternalEventType(normalizeExternalEventType(selectedUser?.externalEventType))
        setBotSettings(normalizeBotSettings(selectedUser?.botSettings))
        setTimetable(normalizeTimetable(selectedUser?.timetable))
        setBotRunning(Boolean(selectedUser?.state))
        setFeedback(null)
        setLogsHidden(false)
    }, [
        selectedUser?.externalEventType,
        selectedUser?.id,
        selectedUser?.name,
        selectedUser?.pass,
        selectedUser?.botSettings,
        selectedUser?.platform,
        selectedUser?.plugins,
        selectedUser?.server,
        selectedUser?.state,
        selectedUser?.timetable
    ])

    React.useEffect(() => {
        loadServerInstances().then(options => {
            setServerOptions(previous => options.length > 0 ? options : previous)
        })
    }, [])

    React.useEffect(() => {
        const nextRunning = Boolean(selectedUser?.state)
        setBotRunning(nextRunning)
        if (nextRunning)
            setActiveSection(SECTION_IDS.liveCastle)
    }, [selectedUser?.state])

    const logText = React.useMemo(() => normalizeLogBuffer(logs?.buffer), [logs?.buffer])

    React.useEffect(() => {
        if (!liveOpsActive || logsHidden || !autoScroll || !logViewportRef.current)
            return

        logViewportRef.current.scrollTop = logViewportRef.current.scrollHeight
    }, [autoScroll, liveOpsActive, logText, logsHidden])

    React.useEffect(() => {
        if (logText)
            setLogsHidden(false)
    }, [logText])

    const allPlugins = React.useMemo(() => Array.isArray(plugins) ? plugins : [], [plugins])
    const isBthSelected = isBthInfluenceModuleVisible(externalEventType)
    const visiblePlugins = React.useMemo(() => getPluginManagerVisiblePlugins(allPlugins), [allPlugins])
    const activePlugins = React.useMemo(
        () => visiblePlugins.filter(plugin => isPluginActive(plugin, userPlugins)),
        [userPlugins, visiblePlugins]
    )
    const pluginDefinitionsByKey = React.useMemo(
        () => new Map(allPlugins.map(plugin => [plugin.key, plugin])),
        [allPlugins]
    )
    const routedModuleActiveState = React.useMemo(() => getRoutedModuleActiveState(userPlugins), [userPlugins])
    const autoSpeedEnabled = routedModuleActiveState.speedSettings

    const dynamicSidebarSections = React.useMemo(
        () => getDynamicSidebarSections(allPlugins, userPlugins),
        [allPlugins, userPlugins]
    )

    const moduleSections = React.useMemo(() => {
        const nextSections = []
        const seenSectionKeys = new Set()
        const hasTowerBotSection =
            activePlugins.some(plugin => TOWER_BOT_PLUGIN_KEYS.has(plugin.key)) ||
            hasAnyPluginStateEnabled(userPlugins, TOWER_BOT_PLUGIN_KEYS)
        const hasProduceComponentsSection =
            activePlugins.some(plugin => PRODUCE_COMPONENTS_PLUGIN_KEYS.has(plugin.key)) ||
            hasAnyPluginStateEnabled(userPlugins, PRODUCE_COMPONENTS_PLUGIN_KEYS)
        const hasFortressBotSection =
            activePlugins.some(plugin => FORTRESS_BOT_PLUGIN_KEYS.has(plugin.key)) ||
            hasAnyPluginStateEnabled(userPlugins, FORTRESS_BOT_PLUGIN_KEYS)

        const pushSection = ({ pluginKey, label, description, icon, id }) => {
            if (!pluginKey || seenSectionKeys.has(pluginKey))
                return

            seenSectionKeys.add(pluginKey)
            nextSections.push({
                id: id ?? `module:${pluginKey}`,
                label,
                description,
                pluginKey,
                icon
            })
        }

        if (isBthSelected)
            pushSection({ pluginKey: BTH_INFLUENCE_ATTACK_PLUGIN_KEY, ...MODULE_SECTION_META[BTH_INFLUENCE_ATTACK_PLUGIN_KEY] })
        if (isBthSelected)
            pushSection({ pluginKey: 'bth_influence_hunter', label: 'BTH Influence Hunter', description: 'Independent player farming and capital planner' })

        if (hasTowerBotSection)
            pushSection({
                pluginKey: TOWER_BOT_SECTION_KEY,
                ...MODULE_SECTION_META[TOWER_BOT_SECTION_KEY],
                label: isBthSelected ? 'BTH Tower Bot Settings' : 'Tower Bot Settings'
            })

        if (hasProduceComponentsSection)
            pushSection({ pluginKey: PRODUCE_COMPONENTS_SECTION_KEY, ...MODULE_SECTION_META[PRODUCE_COMPONENTS_SECTION_KEY] })

        if (hasFortressBotSection)
            pushSection({ pluginKey: FORTRESS_BOT_SECTION_KEY, ...MODULE_SECTION_META[FORTRESS_BOT_SECTION_KEY] })

        if (routedModuleActiveState.nomadsBot)
            pushSection({ pluginKey: NOMADS_BOT_SECTION_KEY, ...MODULE_SECTION_META[NOMADS_BOT_SECTION_KEY] })

        if (routedModuleActiveState.samuraiBot)
            pushSection({ pluginKey: SAMURAI_BOT_SECTION_KEY, ...MODULE_SECTION_META[SAMURAI_BOT_SECTION_KEY] })

        if (autoSpeedEnabled)
            pushSection({
                id: SECTION_IDS.speed,
                pluginKey: AUTO_SPEED_PLUGIN_KEY,
                ...MODULE_SECTION_META[AUTO_SPEED_PLUGIN_KEY]
            })

        activePlugins
            .filter(plugin => plugin.key !== AUTO_SPEED_PLUGIN_KEY)
            .filter(plugin => plugin.key !== BTH_INFLUENCE_ATTACK_PLUGIN_KEY)
            .filter(plugin => plugin.key !== 'bth_influence_hunter')
            .filter(plugin => !CAMP_BOT_RUNTIME_PLUGIN_KEYS.has(plugin.key))
            .filter(plugin => !CORE_SETTINGS_PLUGIN_KEYS.has(plugin.key))
            .filter(plugin => !TOWER_BOT_PLUGIN_KEYS.has(plugin.key))
            .filter(plugin => !PRODUCE_COMPONENTS_PLUGIN_KEYS.has(plugin.key))
            .filter(plugin => !FORTRESS_BOT_PLUGIN_KEYS.has(plugin.key))
            .filter(plugin => !STORM_BOT_HIDDEN_PLUGIN_KEYS.has(plugin.key))
            .forEach(plugin => {
                pushSection({
                    pluginKey: plugin.key,
                    label: plugin.key === 'grabtools' ?
                        (isBthSelected ? 'BTH Master Blacksmith' : 'Master Blacksmith') :
                        plugin.key === 'equipment_seller' ?
                            (isBthSelected ? 'BTH Equipment Seller Settings' : 'Equipment Seller Settings') :
                            (MODULE_SECTION_META[plugin.key]?.label ?? plugin.name),
                    description: MODULE_SECTION_META[plugin.key]?.description ?? 'Enable, disable, and fine-tune your installed modules',
                    icon: MODULE_SECTION_META[plugin.key]?.icon
                })
            })

        Object.entries(userPlugins ?? {}).forEach(([pluginKey, pluginState]) => {
            if (!pluginState?.state)
                return

            if (pluginKey === AUTO_SPEED_PLUGIN_KEY)
                return

            if (pluginKey === BTH_INFLUENCE_ATTACK_PLUGIN_KEY)
                return
            if (pluginKey === 'bth_influence_hunter')
                return

            if (CAMP_BOT_RUNTIME_PLUGIN_KEYS.has(pluginKey))
                return

            if (TOWER_BOT_PLUGIN_KEYS.has(pluginKey) ||
                PRODUCE_COMPONENTS_PLUGIN_KEYS.has(pluginKey) ||
                FORTRESS_BOT_PLUGIN_KEYS.has(pluginKey) ||
                STORM_BOT_HIDDEN_PLUGIN_KEYS.has(pluginKey))
                return

            const staticSection = MODULE_SECTION_META[pluginKey]
            if (!staticSection)
                return

            pushSection({ pluginKey, ...staticSection })
        })

        return nextSections
    }, [activePlugins, autoSpeedEnabled, isBthSelected, routedModuleActiveState, userPlugins])

    const selectedPluginKey = activeSection.startsWith('module:') ? activeSection.slice('module:'.length) : null
    const selectedPlugin = React.useMemo(() => {
        if (!selectedPluginKey)
            return null

        const visibleSection = moduleSections.find(section => section.pluginKey === selectedPluginKey)
        if (visibleSection)
            return visibleSection

        if (selectedPluginKey === TOWER_BOT_SECTION_KEY)
            return {
                pluginKey: selectedPluginKey,
                ...MODULE_SECTION_META[TOWER_BOT_SECTION_KEY],
                label: isBthSelected ? 'BTH Tower Bot Settings' : 'Tower Bot Settings'
            }

        if (selectedPluginKey === 'barracks_modified')
            return {
                pluginKey: selectedPluginKey,
                ...MODULE_SECTION_META.barracks_modified,
                label: isBthSelected ? 'BTH Recruit Bot Settings' : 'Recruit Bot Settings'
            }

        if (selectedPluginKey === 'grabtools')
            return {
                pluginKey: selectedPluginKey,
                ...MODULE_SECTION_META.grabtools,
                label: isBthSelected ? 'BTH Master Blacksmith' : 'Master Blacksmith'
            }

        if (selectedPluginKey === 'equipment_seller')
            return {
                pluginKey: selectedPluginKey,
                ...MODULE_SECTION_META.equipment_seller,
                label: isBthSelected ? 'BTH Equipment Seller Settings' : 'Equipment Seller Settings'
            }

        const staticSection = MODULE_SECTION_META[selectedPluginKey]
        if (staticSection)
            return {
                pluginKey: selectedPluginKey,
                ...staticSection
            }

        if (FORTRESS_BOT_PLUGIN_KEYS.has(selectedPluginKey))
            return {
                pluginKey: selectedPluginKey,
                ...MODULE_SECTION_META[FORTRESS_BOT_SECTION_KEY]
            }

        if (STORM_BOT_HIDDEN_PLUGIN_KEYS.has(selectedPluginKey))
            return {
                pluginKey: STORM_BOT_PRIMARY_PLUGIN_KEY,
                ...MODULE_SECTION_META[STORM_BOT_PRIMARY_PLUGIN_KEY]
            }

        const pluginDefinition = pluginDefinitionsByKey.get(selectedPluginKey)
        if (!pluginDefinition)
            return null

        return {
            pluginKey: selectedPluginKey,
            label: pluginDefinition.name,
            description: pluginDefinition.description ?? 'Enable, disable, and fine-tune your installed modules',
            icon: undefined
        }
    }, [isBthSelected, moduleSections, pluginDefinitionsByKey, selectedPluginKey])
    const lastSeenAt = selectedUserStatus?.lastSeenAt ?? selectedUserStatus?.startTime ?? null
    const serverSelectOptions = serverOptions
    const isNewUser = !(selectedUser?.id || createdBotId)
    const passwordHidden = Boolean(selectedUser?.passwordHidden && !isNewUser)
    const visibleLogText = logsHidden ? '' : logText
    const activeTimetableBlocks = React.useMemo(() => countActiveTimetableBlocks(timetable), [timetable])
    const activeDynamicSection = dynamicSidebarSections.find(section => section.id === activeSection)
    const attackSidebarSection = dynamicSidebarSections.find(section => section.id === SECTION_IDS.attack)
    const commanderSidebarSection = dynamicSidebarSections.find(section => section.id === SECTION_IDS.commander)
    const callSidebarSection = dynamicSidebarSections.find(section => section.id === SECTION_IDS.call)
    const notificationsSidebarSection = dynamicSidebarSections.find(section => section.id === SECTION_IDS.notifications)
    const handleLayoutViewChange = React.useCallback(nextView => {
        onViewChange?.(nextView)
        onClose?.()
    }, [onClose, onViewChange])

    React.useEffect(() => {
        const alwaysVisibleSections = new Set([
            SECTION_IDS.bot,
            SECTION_IDS.liveCastle,
            SECTION_IDS.credentials,
            SECTION_IDS.speed,
            SECTION_IDS.crafting,
            SECTION_IDS.luckyWheel,
            SECTION_IDS.attack,
            SECTION_IDS.commander,
            SECTION_IDS.notifications,
            SECTION_IDS.functions
        ])

        alwaysVisibleSections.add(SECTION_IDS.liveDeco)

        if (showCallSection)
            alwaysVisibleSections.add(SECTION_IDS.call)

        if (showAllianceCallSection)
            alwaysVisibleSections.add(SECTION_IDS.allianceCall)

        if (showAllianceRiftPortalSection)
            alwaysVisibleSections.add(SECTION_IDS.allianceRiftPortal)

        if (showGrandTournamentSection)
            alwaysVisibleSections.add(SECTION_IDS.grandTournament)

        alwaysVisibleSections.add(`module:${TOWER_BOT_SECTION_KEY}`)
        alwaysVisibleSections.add(`module:${PRODUCE_COMPONENTS_SECTION_KEY}`)
        STATIC_MODULE_SECTION_KEYS.forEach(sectionKey => {
            if (sectionKey !== BTH_INFLUENCE_ATTACK_PLUGIN_KEY || isBthSelected)
                alwaysVisibleSections.add(`module:${sectionKey}`)
        })
        visiblePlugins.forEach(plugin => {
            if (plugin?.key && (isBthSelected || ![...TOWER_BOT_PLUGIN_KEYS, BTH_INFLUENCE_ATTACK_PLUGIN_KEY].includes(plugin.key)))
                alwaysVisibleSections.add(`module:${plugin.key}`)
        })
        dynamicSidebarSections.forEach(section => alwaysVisibleSections.add(section.id))
        moduleSections.forEach(section => alwaysVisibleSections.add(section.id))

        const redirectedPluginKey = activeSection.startsWith('module:') ? activeSection.slice('module:'.length) : ''
        const redirectedCoreSection = ALLIANCE_CALL_CORE_PLUGIN_KEYS.has(redirectedPluginKey) ?
            (showAllianceCallSection ? SECTION_IDS.allianceCall : SECTION_IDS.functions) :
            CALL_SETUP_CORE_PLUGIN_KEYS.has(redirectedPluginKey) ?
                (showCallSection ? SECTION_IDS.call : SECTION_IDS.functions) :
                ''

        if (STORM_BOT_HIDDEN_PLUGIN_KEYS.has(redirectedPluginKey)) {
            setActiveSection(`module:${STORM_BOT_PRIMARY_PLUGIN_KEY}`)
            return
        }

        if (redirectedPluginKey === BTH_INFLUENCE_ATTACK_PLUGIN_KEY && !isBthSelected) {
            setActiveSection(SECTION_IDS.functions)
            return
        }

        if (redirectedCoreSection) {
            setActiveSection(redirectedCoreSection)
            return
        }

        if (!alwaysVisibleSections.has(activeSection))
            setActiveSection(SECTION_IDS.functions)
    }, [activeSection, dynamicSidebarSections, isAdmin, isBthSelected, moduleSections, showAllianceCallSection, showAllianceRiftPortalSection, showCallSection, showGrandTournamentSection, visiblePlugins])

    const focusEditor = () => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    const buildPayload = React.useCallback((stateOverride = botRunning, overrides = {}) => {
        const trimmedName = String(name ?? '').trim()

        if (!trimmedName) {
            setFeedback({ severity: 'error', message: t('Please enter a bot name before saving.') })
            return null
        }

        if (isNewUser && !String(pass ?? '').trim()) {
            setFeedback({ severity: 'error', message: t('Please set a password for new bots before saving.') })
            return null
        }

        const normalizedEventType = normalizeExternalEventType(externalEventType)
        const payload = {
            ...selectedUser,
            name: trimmedName,
            state: stateOverride ? 1 : 0,
            plugins: cloneValue(overrides.plugins ?? userPlugins),
            platform,
            server: server === '' ? undefined : Number(server),
            externalEventType: normalizedEventType,
            externalEvent: Boolean(normalizedEventType),
            botSettings: normalizeBotSettings(overrides.botSettings ?? botSettings),
            timetable: normalizeTimetable(timetable)
        }

        if (!passwordHidden && String(pass ?? '').trim())
            payload.pass = pass

        // Access grants are managed separately. Do not let a stale drawer snapshot
        // revoke an approval during an ordinary configuration save.
        delete payload.grandTournamentApproved

        return payload
    }, [botRunning, botSettings, externalEventType, isNewUser, name, pass, passwordHidden, platform, selectedUser, server, t, timetable, userPlugins])

    const handleSave = React.useCallback(({ closeAfterSave = false } = {}) => {
        const payload = buildPayload(botRunning)

        if (!payload)
            return false

        sendBotConfiguration(payload)
        setFeedback({ severity: 'success', message: t('Configuration saved locally and sent to the server.') })

        if (closeAfterSave)
            onClose?.()

        return true
    }, [botRunning, buildPayload, isNewUser, onClose, sendBotConfiguration, t, ws])

    const handleSavePlugins = React.useCallback(nextPluginsValue => {
        const normalizedPlugins = cloneValue(nextPluginsValue)
        const payload = buildPayload(botRunning, { plugins: normalizedPlugins })

        if (!payload)
            return false

        setUserPlugins(normalizedPlugins)
        sendBotConfiguration(payload)
        setFeedback({ severity: 'success', message: t('Configuration saved locally and sent to the server.') })

        return true
    }, [botRunning, buildPayload, isNewUser, sendBotConfiguration, t, ws])

    const handleImmediateBotSettingsSave = React.useCallback((nextBotSettings, options = {}) => {
        const normalizedSettings = normalizeBotSettings(nextBotSettings)

        if (isNewUser) {
            setBotSettings(normalizedSettings)
            return false
        }

        const payload = buildPayload(botRunning, { botSettings: normalizedSettings })

        if (!payload)
            return false

        if (options.grantGrandTournamentApproval)
            payload.grantGrandTournamentApproval = true

        setBotSettings(normalizedSettings)
        sendBotConfiguration(payload)
        setFeedback({ severity: 'success', message: t('Configuration saved locally and sent to the server.') })

        return true
    }, [botRunning, buildPayload, isNewUser, sendBotConfiguration, t, ws])

    const handleToggleRunning = () => {
        const nextRunning = !botRunning
        const payload = buildPayload(nextRunning)

        if (!payload)
            return

        setBotRunning(nextRunning)
        sendBotConfiguration(payload)
        if (nextRunning) {
            setActiveSection(SECTION_IDS.liveCastle)
            setLogsHidden(false)
            if (!liveOpsActive)
                onStartLiveOps?.()
        }
    }

    const handleManualCraftingStart = React.useCallback(nextBotSettings => {
        const normalizedSettings = normalizeBotSettings(nextBotSettings)
        const payload = buildPayload(true, { botSettings: normalizedSettings })

        if (!payload)
            return

        setBotRunning(true)
        setBotSettings(normalizedSettings)
        sendBotConfiguration(payload)
        setActiveSection(SECTION_IDS.liveCastle)
        setLogsHidden(false)
        if (!liveOpsActive)
            onStartLiveOps?.()
        setFeedback({ severity: 'success', message: t('Crafting start request sent.') })
    }, [buildPayload, isNewUser, liveOpsActive, onStartLiveOps, sendBotConfiguration, t, ws])

    const handleOpenLiveOps = () => {
        if (!liveOpsActive)
            onStartLiveOps?.()

        liveOpsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const handleExport = () => {
        const payload = buildPayload(botRunning)

        if (!payload)
            return

        downloadTextFile(
            `${String(payload.name ?? 'bot-config').replace(/\s+/g, '-').toLowerCase()}.json`,
            JSON.stringify({ ...payload, exportedAt: new Date().toISOString() }, null, 2),
            'application/json;charset=utf-8'
        )
    }

    const handleExportLogs = () => {
        if (!visibleLogText)
            return

        downloadTextFile(
            `${String(name || selectedUser?.name || 'bot').replace(/\s+/g, '-').toLowerCase()}-live-ops.log`,
            visibleLogText
        )
    }

    const handleOpenTemplates = () => {
        const templatePlugin = visiblePlugins.find(plugin =>
            Array.isArray(plugin?.pluginOptions) &&
            plugin.pluginOptions.some(option => option.key === 'attackPresetSlot'))

        setActiveSection(templatePlugin ? `module:${templatePlugin.key}` : SECTION_IDS.functions)
        focusEditor()
    }

    const handleImport = async event => {
        const file = event.target.files?.[0]

        if (!file)
            return

        try {
            const imported = JSON.parse(await file.text())
            setName(imported.name ?? selectedUser?.name ?? '')
            setPass(Object.hasOwn(imported ?? {}, 'pass') ? (imported.pass ?? '') : (selectedUser?.pass ?? ''))
            setUserPlugins(cloneValue(imported.plugins ?? {}))
            setPlatform(imported.platform === 'e4k' ? 'e4k' : 'gge')
            setServer(imported.server ?? '')
            setExternalEventType(normalizeExternalEventType(imported.externalEventType))
            setBotSettings(normalizeBotSettings(imported.botSettings))
            setTimetable(normalizeTimetable(imported.timetable))
            setBotRunning(Boolean(imported.state))
            setFeedback({ severity: 'info', message: t('Imported configuration draft. Review it and press Save.') })
            setActiveSection(SECTION_IDS.bot)
            focusEditor()
        }
        catch (error) {
            console.warn('Unable to import bot configuration', error)
            setFeedback({ severity: 'error', message: t('Unable to read that file. Please import a JSON export from this page.') })
        }
        finally {
            event.target.value = ''
        }
    }

    const sectionTitle = activeSection === SECTION_IDS.bot ? t('Bot Settings') :
        activeSection === SECTION_IDS.allianceRiftPortal ? t('Alliance RIFT Portal') :
            activeSection === SECTION_IDS.grandTournament ? t('Grand Tournament') :
            activeSection === SECTION_IDS.liveCastle ? t('LIVE Castle') :
            activeSection === SECTION_IDS.liveDeco ? 'LIVE Deco' :
            activeSection === SECTION_IDS.credentials ? t('User Credentials & Server Settings') :
                    activeSection === SECTION_IDS.speed ? t('Speed Settings') :
                        activeSection === SECTION_IDS.crafting ? t('Commanders & Castellans Crafting') :
                            activeSection === SECTION_IDS.luckyWheel ? t('Lucky Wheel') :
                                activeSection === SECTION_IDS.allianceCall ? t('Alliance Call Alert') :
                        activeSection === SECTION_IDS.functions ? t('Function Selection') :
                            activeDynamicSection ? t(activeDynamicSection.label) :
                                (selectedPlugin?.label ?? t('Function Selection'))

    const sectionDescription = activeSection === SECTION_IDS.bot ? t('Basic bot behavior and timing settings') :
        activeSection === SECTION_IDS.allianceRiftPortal ? t('Enable or disable portal access for this bot account') :
            activeSection === SECTION_IDS.grandTournament ? t('Automate the separate Grand Tournament quest board and Quest Ticket') :
            activeSection === SECTION_IDS.liveCastle ? t('Live castle overview for troops and tools') :
            activeSection === SECTION_IDS.liveDeco ? 'Planifică amplasarea clădirilor și decorațiunilor din castelele tale.' :
            activeSection === SECTION_IDS.credentials ? t('Login credentials and game server configuration') :
                    activeSection === SECTION_IDS.speed ? t('Activate the Global Effect for a Speed Boost automatically') :
                        activeSection === SECTION_IDS.crafting ? t('Configure separate crafting priorities for commanders and castellans.') :
                            activeSection === SECTION_IDS.luckyWheel ? t('Queue spin runs and inspect the latest recorded wheel rewards.') :
                                activeSection === SECTION_IDS.call ? t('Configure Player Alert for approved accounts') :
                            activeSection === SECTION_IDS.allianceCall ? t('Configure Alliance Player Alert for approved accounts') :
                        activeSection === SECTION_IDS.notifications ? t('Configure Discord webhook notifications') :
                        activeSection === SECTION_IDS.attack ? t('Attack template selections for different bot types') :
                            activeSection === SECTION_IDS.functions ? t('Enable or disable bot functions') :
                                activeDynamicSection ? t(activeDynamicSection.description) :
                                    (selectedPlugin?.description ? selectedPlugin.description : t('Enable, disable, and fine-tune your installed modules'))

    const renderPluginManager = ({ pluginList = plugins, ...extraProps } = {}) => (
        <PluginManager
            plugins={pluginList}
            userPlugins={userPlugins}
            onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
            channels={channels}
            selectedUser={selectedUser}
            ws={ws}
            onSave={handleSave}
            availableCastleTargets={selectedUserStatus?.barracksTargets}
            availableAttackPresets={selectedUserStatus?.availableAttackPresets}
            availableAttackTemplateInventory={selectedUserStatus?.attackTemplateInventory}
            appLanguage={appLanguage}
            templatePlatform={platform}
            {...extraProps}
        />
    )

    const renderSectionReadyPlaceholder = () => (
        <Box
            sx={{
                p: 2.2,
                borderRadius: 2.6,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)'
            }}
        >
            <Typography variant="h6" sx={{ fontSize: '1.02rem' }}>
                {t('This section is ready.')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary', maxWidth: 620 }}>
                {t('Send the design and required functions for this section and I will build it here.')}
            </Typography>
        </Box>
    )

    const renderCoreSection = () => {
        if (activeSection === SECTION_IDS.liveDeco)
            return <LiveDecoPanel key={selectedUser?.id} user={selectedUser} />
        if (activeSection === SECTION_IDS.bot) {
            return (
                <BotSettingsPanel
                    value={botSettings}
                    onChange={setBotSettings}
                    onImmediateSave={handleImmediateBotSettingsSave}
                    appLanguage={appLanguage}
                />
            )
        }

        if (activeSection === SECTION_IDS.allianceRiftPortal && showAllianceRiftPortalSection) {
            return (
                <AllianceRiftPortalPanel
                    value={botSettings.allianceRiftPortal}
                    commanderWhiteList={userPlugins?.[ALLIANCE_RIFT_PORTAL_PLUGIN_KEY]?.commanderWhiteList}
                    runtimeStatus={selectedUserStatus?.allianceRiftPortal}
                    onOpenCommanderAssignment={() => {
                        setActiveSection(SECTION_IDS.commander)
                        focusEditor()
                    }}
                    onChange={nextPortalSettings => {
                        const nextSettings = normalizeBotSettings({
                            ...botSettings,
                            enableAllianceRiftPortal: Boolean(botSettings.enableAllianceRiftPortal || nextPortalSettings.attackPortal || nextPortalSettings.shopEnabled),
                            allianceRiftPortal: nextPortalSettings
                        })

                        setBotSettings(nextSettings)
                        handleImmediateBotSettingsSave(nextSettings)
                    }}
                    appLanguage={appLanguage}
                />
            )
        }

        if (activeSection === SECTION_IDS.grandTournament && showGrandTournamentSection) {
            return (
                <GrandTournamentPanel
                    value={botSettings.grandTournament}
                    enabled={Boolean(botSettings.enableGrandTournament)}
                    runtimeStatus={selectedUserStatus?.grandTournament}
                    onEnabledChange={nextEnabled => {
                        const nextSettings = normalizeBotSettings({
                            ...botSettings,
                            enableGrandTournament: nextEnabled
                        })
                        setBotSettings(nextSettings)
                        handleImmediateBotSettingsSave(nextSettings, {
                            grantGrandTournamentApproval: shouldRequestGrandTournamentApproval(isAdmin, nextEnabled)
                        })
                    }}
                    onChange={nextTournamentSettings => {
                        const nextSettings = normalizeBotSettings({
                            ...botSettings,
                            grandTournament: nextTournamentSettings
                        })
                        setBotSettings(nextSettings)
                        handleImmediateBotSettingsSave(nextSettings)
                    }}
                    appLanguage={appLanguage}
                />
            )
        }

        if (activeSection === SECTION_IDS.liveCastle) {
            return (
                <CoreLocationsPanel
                    runtimeStatus={selectedUserStatus}
                    t={t}
                />
            )
        }

        if (activeSection === SECTION_IDS.credentials) {
            return (
                <Stack spacing={3}>
                    <UserCredentialsPanel
                        username={name}
                        password={pass}
                        onUsernameChange={setName}
                        onPasswordChange={setPass}
                        passwordHidden={passwordHidden}
                        passwordDisabled={passwordHidden}
                        appLanguage={appLanguage}
                    />
                    <ServerSettingsPanel
                        server={server}
                        serverOptions={serverSelectOptions}
                        platform={platform}
                        externalEventType={externalEventType}
                        onServerChange={setServer}
                        onPlatformChange={setPlatform}
                        onExternalEventTypeChange={setExternalEventType}
                        appLanguage={appLanguage}
                    />
                </Stack>
            )
        }

        if (activeSection === SECTION_IDS.speed) {
            return (
                <SpeedSettingsPanel
                    enabled={autoSpeedEnabled}
                    onChange={nextValue => {
                        setUserPlugins(previous => {
                            const nextPlugins = cloneValue(previous)
                            nextPlugins[AUTO_SPEED_PLUGIN_KEY] ??= {}
                            nextPlugins[AUTO_SPEED_PLUGIN_KEY].state = nextValue
                            nextPlugins[AUTO_SPEED_PLUGIN_KEY].enabled = nextValue
                            return nextPlugins
                        })
                    }}
                    appLanguage={appLanguage}
                />
            )
        }

        if (activeSection === SECTION_IDS.crafting) {
            return (
                <CommandersCastellansCraftingPanel
                    value={botSettings}
                    onChange={setBotSettings}
                    onManualStart={handleManualCraftingStart}
                    botRunning={botRunning}
                    botStandbyActive={selectedUserStatus?.standbyActive}
                    botStandbyReason={selectedUserStatus?.standbyReason}
                    runtimeStatus={selectedUserStatus?.commandersCastellansCrafting}
                    availableCastleTargets={selectedUserStatus?.barracksTargets}
                    appLanguage={appLanguage}
                />
            )
        }

        if (activeSection === SECTION_IDS.luckyWheel) {
            return (
                <LuckyWheelSettingsPanel
                    selectedUser={selectedUser}
                    runtimeStatus={selectedUserStatus?.luckyWheel}
                    botRunning={botRunning}
                    ws={ws}
                    appLanguage={appLanguage}
                    onRequestLiveOps={onStartLiveOps}
                />
            )
        }

        if (activeSection === SECTION_IDS.functions)
            return (
                <FunctionSelectionPanel
                    plugins={plugins}
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    onModuleSelect={nextSection => {
                        setActiveSection(nextSection)
                        focusEditor()
                    }}
                    appLanguage={appLanguage}
                    bthMode={isBthSelected}
                />
            )

        return null
    }

    const renderSectionContent = () => {
        if (selectedPluginKey === 'bth_influence_hunter' && isBthSelected)
            return <BthInfluenceHunterPanel userPlugins={userPlugins}
                onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                runtimeStatus={selectedUserStatus?.bthInfluenceHunter}
                commanderRoster={selectedUserStatus?.commanderRoster} />
        if (selectedPluginKey === BTH_INFLUENCE_ATTACK_PLUGIN_KEY && isBthSelected)
            return (
                <BthInfluenceAttackPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.bthInfluenceAttack}
                />
            )

        if (selectedPluginKey === 'tax_collector' && !isBthSelected)
            return (
                <BthTaxCollectorSettingsPanel
                    pluginKey="tax_collector"
                    title="Automatic Tax Collector"
                    description="Collect taxes and automatically start the next period in the normal game."
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.taxCollector}
                />
            )

        if (selectedPluginKey === BTH_TAX_COLLECTOR_PLUGIN_KEY && isBthSelected)
            return (
                <BthTaxCollectorSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.bthTaxCollector}
                />
            )

        if (selectedPluginKey === BTH_MAP_ATTACK_PLUGIN_KEY && isBthSelected)
            return (
                <BthMapAttackSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.bthMapAttack}
                />
            )

        if (selectedPluginKey === TOWER_BOT_SECTION_KEY)
            return (
                <TowerBotSettingsPanel
                    appLanguage={appLanguage}
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    templatePlatform={platform}
                    bthMode={isBthSelected}
                />
            )

        if (selectedPluginKey === 'flee')
            return (
                <CastleDefenseSettingsPanel
                    pluginKey="flee"
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (selectedPluginKey === PRODUCE_COMPONENTS_SECTION_KEY)
            return (
                <ProduceComponentsSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                />
            )

        if (selectedPluginKey === FORTRESS_BOT_SECTION_KEY || FORTRESS_BOT_PLUGIN_KEYS.has(selectedPluginKey))
            return (
                <FortressBotSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                />
            )

        if (selectedPluginKey === STORM_BOT_PRIMARY_PLUGIN_KEY || STORM_BOT_HIDDEN_PLUGIN_KEYS.has(selectedPluginKey))
            return (
                <StormBotSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.stormCargo}
                    dailyAttackStatus={selectedUserStatus?.dailyAttackLimits?.stormAquaForts}
                    appLanguage={appLanguage}
                    templatePlatform={platform}
                />
            )

        if (selectedPluginKey === NOMADS_BOT_SECTION_KEY || selectedPluginKey === SAMURAI_BOT_SECTION_KEY)
            return (
                <CampBotSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    templatePlatform={platform}
                    eventType={selectedPluginKey === SAMURAI_BOT_SECTION_KEY ? 'samurai' : 'nomad'}
                />
            )

        if (selectedPluginKey === 'attackberi')
            return (
                <BerriGreenSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                />
            )

        if (selectedPluginKey === 'attackaliens')
            return (
                <ForeignCastlesBotSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.strainiCorbi}
                />
            )

        if (selectedPluginKey === 'berikingdom')
            return (
                <BerriWorldSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (selectedPluginKey === 'barracks_modified')
            return (
                <RecruitBotSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    availableTargets={selectedUserStatus?.barracksTargets}
                    plugins={plugins}
                    appLanguage={appLanguage}
                    bthMode={isBthSelected}
                />
            )

        if (selectedPluginKey === 'feast')
            return (
                <AutomaticFeastSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (selectedPluginKey === 'meadrep')
            return (
                <FoodCheckSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (selectedPluginKey === 'hospital')
            return (
                <HospitalHealerSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                    hideHeader
                />
            )

        if (selectedPluginKey === 'auto_recruit_tools')
            return (
                <ToolBuildSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    plugins={plugins}
                    appLanguage={appLanguage}
                    hideHeader
                />
            )

        if (selectedPluginKey === 'equipment_seller')
            return (
                <EquipmentSellerSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    onSave={handleSavePlugins}
                    selectedUser={selectedUser}
                />
            )

        if (selectedPluginKey === 'attackkhan')
            return (
                <KhanAttackConfigurator
                    parentKey="attackkhan"
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                    hideWorkspaceHeader
                    templatePlatform={platform}
                />
            )

        if (selectedPluginKey === 'skips_trade')
            return (
                <SkipsTradeSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                />
            )

        if (selectedPluginKey === 'nativevillage')
            return (
                <NativeVillageSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.nativeVillages}
                />
            )

        if (selectedPluginKey === 'armorer')
            return (
                <ArmorerSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                />
            )

        if (selectedPluginKey === 'grabtools')
            return (
                <MasterBlacksmithSettingsPanel
                    bthMode={isBthSelected}
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.masterBlacksmith}
                    botRunning={botRunning}
                />
            )

        if (selectedPluginKey === 'technicus')
            return (
                <TechnicusSettingsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    runtimeStatus={selectedUserStatus?.technicus}
                />
            )

        if (selectedPluginKey)
            return renderPluginManager({ activePluginKey: selectedPluginKey, hideFilters: true, singleColumn: true, defaultExpandedAll: true })

        if (activeSection === SECTION_IDS.attack)
            return (
                <AttackSetupPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                    templatePlatform={platform}
                />
            )

        if (activeSection === SECTION_IDS.commander)
            return (
                <CommanderAllocationPanel
                    plugins={[
                        ...(commanderSidebarSection?.plugins ?? []).filter(plugin =>
                            plugin?.key !== BTH_INFLUENCE_ATTACK_PLUGIN_KEY || isBthSelected),
                        ALLIANCE_RIFT_COMMANDER_PLUGIN
                    ]}
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    commanderRoster={selectedUserStatus?.commanderRoster}
                    appLanguage={appLanguage}
                />
            )

        if (activeSection === SECTION_IDS.call)
            return (
                <CallSetupPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (activeSection === SECTION_IDS.allianceCall)
            return (
                <AllianceCallAlertPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (activeSection === SECTION_IDS.notifications)
            return (
                <DiscordNotificationsPanel
                    userPlugins={userPlugins}
                    onChange={nextValue => setUserPlugins(cloneValue(nextValue))}
                    appLanguage={appLanguage}
                />
            )

        if (activeDynamicSection)
            return activeDynamicSection.plugins.length > 0 ?
                renderPluginManager({ pluginList: activeDynamicSection.plugins, hideFilters: true, singleColumn: true, defaultExpandedAll: true }) :
                renderSectionReadyPlaceholder()

        return renderCoreSection()
    }

    return (
        <MainLayout
            currentView={currentView}
            isAdmin={isAdmin}
            onViewChange={handleLayoutViewChange}
            accountInfo={accountInfo}
            appLanguage={appLanguage}
            onAppLanguageChange={onAppLanguageChange}
        >
            <Box
                sx={{
                    minHeight: '100%',
                    background: 'linear-gradient(180deg, rgba(17,26,44,0.98) 0%, rgba(24,35,58,0.98) 100%)',
                    mx: { xs: -2, md: -3.75 },
                    mt: { xs: -2, md: -3.75 },
                    mb: -5,
                    px: { xs: 2, md: 3.75 },
                    pt: { xs: 2.5, md: 3.5 },
                    pb: 5
                }}
            >
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', xl: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', xl: 'flex-start' }}
                    >
                        <Stack spacing={1.1}>
                            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3.1rem' }, letterSpacing: '-0.05em' }}>
                                {`${t('Configure')} ${selectedUser?.name ?? 'Bot'}`}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 720 }}>
                                {t('Manage your bot\'s configuration, templates, and settings')}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setActiveSection(SECTION_IDS.credentials)
                                    focusEditor()
                                }}
                                sx={{
                                    mt: 1,
                                    width: 'fit-content',
                                    minWidth: 180,
                                    backgroundColor: 'rgba(255,255,255,0.11)',
                                    color: 'text.primary',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Manage User Access')}
                            </Button>
                        </Stack>

                        <ToolbarButton onClick={handleOpenLiveOps} startIcon={<MonitorHeartRoundedIcon />}>
                            {t('Open Live Ops')}
                        </ToolbarButton>
                    </Stack>

                    {feedback && (
                        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ borderRadius: 2.5 }}>
                            {feedback.message}
                        </Alert>
                    )}

                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1.4}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                        justifyContent="space-between"
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <ToolbarButton onClick={onClose} startIcon={<ArrowBackRoundedIcon />}>
                                {t('Back')}
                            </ToolbarButton>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    {`${t('Status')}:`}
                                </Typography>
                                <Chip
                                    label={botRunning ? t('Running') : t('Stopped')}
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.12)',
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                        color: botRunning ? '#8fd3ff' : 'text.secondary'
                                    }}
                                />
                            </Stack>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                            <ToolbarButton
                                variant="contained"
                                color={botRunning ? 'error' : 'success'}
                                startIcon={botRunning ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                                onClick={handleToggleRunning}
                            >
                                {botRunning ? t('Stop') : t('Start')}
                            </ToolbarButton>
                            <ToolbarButton startIcon={<CloudUploadRoundedIcon />} onClick={() => fileInputRef.current?.click()}>
                                {t('Import')}
                            </ToolbarButton>
                            <ToolbarButton startIcon={<CloudDownloadRoundedIcon />} onClick={handleExport}>
                                {t('Export')}
                            </ToolbarButton>
                            <ToolbarButton startIcon={<DescriptionRoundedIcon />} onClick={handleOpenTemplates}>
                                {t('Templates')}
                            </ToolbarButton>
                            <ToolbarButton variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => handleSave()}>
                                {t('Save')}
                            </ToolbarButton>
                        </Stack>
                    </Stack>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/json,.json"
                        style={{ display: 'none' }}
                        onChange={handleImport}
                    />

                    <Grid container spacing={2.5} alignItems="flex-start">
                        <Grid item xs={12} lg={3}>
                            <Paper
                                sx={{
                                    p: 1.3,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(20,31,52,0.92)',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    position: { lg: 'sticky' },
                                    top: { lg: 8 }
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        px: 1.2,
                                        color: 'rgba(167,183,212,0.72)',
                                        letterSpacing: '0.08em'
                                    }}
                                >
                                    {t('Core Settings')}
                                </Typography>
                                <List disablePadding sx={{ mt: 0.7 }}>
                                    <SidebarSection active={activeSection === SECTION_IDS.bot} icon={<SettingsRoundedIcon />} label={t('Bot Settings')} onClick={() => { setActiveSection(SECTION_IDS.bot); focusEditor() }} />
                                    <SidebarSection active={activeSection === SECTION_IDS.liveCastle} icon={<CastleRoundedIcon />} label={t('LIVE Castle')} onClick={() => { setActiveSection(SECTION_IDS.liveCastle); focusEditor() }} />
                                    <SidebarSection active={activeSection === SECTION_IDS.liveDeco} icon={<CastleRoundedIcon />} label="LIVE Deco" onClick={() => { setActiveSection(SECTION_IDS.liveDeco); focusEditor() }} />
                                    <SidebarSection active={activeSection === SECTION_IDS.credentials} icon={<KeyRoundedIcon />} label={t('User Credentials & Server Settings')} onClick={() => { setActiveSection(SECTION_IDS.credentials); focusEditor() }} />
                                    <SidebarSection active={activeSection === SECTION_IDS.crafting} icon={<Inventory2RoundedIcon />} label={t('Commanders & Castellans Crafting')} onClick={() => { setActiveSection(SECTION_IDS.crafting); focusEditor() }} />
                                    <SidebarSection active={activeSection === SECTION_IDS.luckyWheel} icon={<CasinoRoundedIcon />} label={t('Lucky Wheel')} onClick={() => { setActiveSection(SECTION_IDS.luckyWheel); focusEditor() }} />
                                    {attackSidebarSection && (
                                        <SidebarSection
                                            active={activeSection === SECTION_IDS.attack}
                                            icon={attackSidebarSection.icon}
                                            label={t(attackSidebarSection.label)}
                                            onClick={() => { setActiveSection(SECTION_IDS.attack); focusEditor() }}
                                        />
                                    )}
                                    <SidebarSection active={activeSection === SECTION_IDS.functions} icon={<ViewModuleRoundedIcon />} label={t('Function Selection')} onClick={() => { setActiveSection(SECTION_IDS.functions); focusEditor() }} />
                                    {commanderSidebarSection && (
                                        <SidebarSection
                                            active={activeSection === SECTION_IDS.commander}
                                            icon={commanderSidebarSection.icon}
                                            label={t(commanderSidebarSection.label)}
                                            onClick={() => { setActiveSection(SECTION_IDS.commander); focusEditor() }}
                                        />
                                    )}
                                    {showAllianceRiftPortalSection && (
                                        <SidebarSection active={activeSection === SECTION_IDS.allianceRiftPortal} icon={<PublicRoundedIcon />} label={t('Alliance RIFT Portal')} onClick={() => { setActiveSection(SECTION_IDS.allianceRiftPortal); focusEditor() }} />
                                    )}
                                    {showGrandTournamentSection && (
                                        <SidebarSection active={activeSection === SECTION_IDS.grandTournament} icon={<EmojiEventsRoundedIcon />} label={t('Grand Tournament')} onClick={() => { setActiveSection(SECTION_IDS.grandTournament); focusEditor() }} />
                                    )}
                                    {callSidebarSection && showCallSection && (
                                        <SidebarSection
                                            active={activeSection === SECTION_IDS.call}
                                            icon={callSidebarSection.icon}
                                            label={t(callSidebarSection.label)}
                                            onClick={() => { setActiveSection(SECTION_IDS.call); focusEditor() }}
                                        />
                                    )}
                                    {showAllianceCallSection && (
                                        <SidebarSection
                                            active={activeSection === SECTION_IDS.allianceCall}
                                            icon={<PhoneInTalkRoundedIcon />}
                                            label={t('Alliance Call Alert')}
                                            onClick={() => { setActiveSection(SECTION_IDS.allianceCall); focusEditor() }}
                                        />
                                    )}
                                    {notificationsSidebarSection && (
                                        <SidebarSection
                                            active={activeSection === SECTION_IDS.notifications}
                                            icon={notificationsSidebarSection.icon}
                                            label={t(notificationsSidebarSection.label)}
                                            onClick={() => { setActiveSection(SECTION_IDS.notifications); focusEditor() }}
                                        />
                                    )}
                                </List>

                                {moduleSections.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 1.4, borderColor: 'rgba(255,255,255,0.06)' }} />
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                px: 1.2,
                                                color: 'rgba(167,183,212,0.72)',
                                                letterSpacing: '0.08em'
                                            }}
                                        >
                                            {t('Modules')}
                                        </Typography>
                                        <List disablePadding sx={{ mt: 0.7 }}>
                                            {moduleSections.map(section => (
                                                <SidebarSection
                                                    key={section.id}
                                                    active={activeSection === section.id}
                                                    icon={section.icon ? React.createElement(section.icon) : <ExtensionRoundedIcon />}
                                                    label={section.label}
                                                    onClick={() => {
                                                        setActiveSection(section.id)
                                                        focusEditor()
                                                    }}
                                                />
                                            ))}
                                        </List>
                                    </>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} lg={9}>
                            <Stack spacing={2.5}>
                                <Paper
                                    ref={editorRef}
                                    sx={{
                                        p: { xs: 2, md: 3 },
                                        borderRadius: 3.2,
                                        backgroundColor: 'rgba(24,36,60,0.94)',
                                        border: '1px solid rgba(255,255,255,0.07)'
                                    }}
                                >
                                    <Stack spacing={2.4}>
                                        <Box>
                                            <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' } }}>
                                                {t('Configuration Editor')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ mt: 0.55, color: 'text.secondary' }}>
                                                {t('Edit your bot\'s configuration')}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: { xs: 1.6, md: 2.2 },
                                                borderRadius: 3,
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                backgroundColor: 'rgba(255,255,255,0.02)'
                                            }}
                                        >
                                            <Stack spacing={2.25}>
                                                <Stack direction="row" spacing={1.25} alignItems="center">
                                                    {selectedPluginKey ?
                                                        (selectedPlugin?.icon ?
                                                            React.createElement(selectedPlugin.icon, { sx: { color: '#f5f7ff' } }) :
                                                            <ExtensionRoundedIcon sx={{ color: '#f5f7ff' }} />) :
                                                        activeDynamicSection ? React.cloneElement(activeDynamicSection.icon, { sx: { color: '#f5f7ff' } }) :
                                                        activeSection === SECTION_IDS.credentials ? <KeyRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                            activeSection === SECTION_IDS.allianceRiftPortal ? <PublicRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                activeSection === SECTION_IDS.grandTournament ? <EmojiEventsRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                activeSection === SECTION_IDS.liveCastle ? <CastleRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                    activeSection === SECTION_IDS.speed ? <SpeedRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                        activeSection === SECTION_IDS.crafting ? <Inventory2RoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                            activeSection === SECTION_IDS.luckyWheel ? <CasinoRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                        activeSection === SECTION_IDS.functions ? <ViewModuleRoundedIcon sx={{ color: '#f5f7ff' }} /> :
                                                                            <SettingsRoundedIcon sx={{ color: '#f5f7ff' }} />
                                                    }
                                                    <Box>
                                                        <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.45rem' } }}>
                                                            {sectionTitle}
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.3 }}>
                                                            {sectionDescription}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                {renderSectionContent()}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Paper>

                                <Paper
                                    sx={{
                                        p: 3,
                                        borderRadius: 3.2,
                                        backgroundColor: 'rgba(14,24,42,0.92)',
                                        border: '1px solid rgba(255,255,255,0.06)'
                                    }}
                                >
                                    <Stack spacing={1.2}>
                                        <Typography variant="h5" sx={{ fontSize: '1.45rem' }}>
                                            {t('Timetable')}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            {t('Define when the bot should run automatically.')}
                                        </Typography>
                                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                                <Chip
                                                    label={timetable.enabled ? t('Timetable enabled') : t('Timetable disabled')}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.12)',
                                                        backgroundColor: 'rgba(255,255,255,0.04)'
                                                    }}
                                                />
                                                <Chip
                                                    label={`${activeTimetableBlocks} ${t('Active time slots')}`}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.12)',
                                                        backgroundColor: 'rgba(255,255,255,0.04)'
                                                    }}
                                                />
                                                <Chip
                                                    label={timetable.useFiveMinuteBlocks ? '5 min' : '15 min'}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.12)',
                                                        backgroundColor: 'rgba(255,255,255,0.04)'
                                                    }}
                                                />
                                            </Stack>

                                            <Button
                                                variant="contained"
                                                onClick={() => setTimetableOpen(true)}
                                                sx={{
                                                    mt: 1,
                                                    width: 'fit-content',
                                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                                    color: 'text.primary',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                {t('Open Timetable')}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Paper>

                                <Paper
                                    ref={liveOpsRef}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3.2,
                                        backgroundColor: 'rgba(13,23,40,0.92)',
                                        border: '1px solid rgba(255,255,255,0.06)'
                                    }}
                                >
                                    <Stack spacing={2.2}>
                                        <Stack
                                            direction={{ xs: 'column', lg: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'stretch', lg: 'center' }}
                                            spacing={1.5}
                                        >
                                            <Stack direction="row" spacing={1.3} alignItems="center">
                                                <MonitorHeartRoundedIcon sx={{ color: 'primary.main' }} />
                                                <Typography variant="h5" sx={{ fontSize: '1.35rem' }}>
                                                    {t('Bot Monitoring')}
                                                </Typography>
                                                <Chip
                                                    label={botRunning ? t('Running') : t('Stopped')}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.12)',
                                                        color: botRunning ? '#8fd3ff' : 'text.secondary',
                                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                                    }}
                                                />
                                            </Stack>
                                            <ToolbarButton
                                                onClick={() => {
                                                    try {
                                                        if (liveOpsActive)
                                                            onStopLiveOps?.()
                                                        else
                                                            onStartLiveOps?.()
                                                    }
                                                    catch (error) {
                                                        console.warn('Unable to toggle live ops', error)
                                                        setFeedback({ severity: 'error', message: t('Unable to start live polling right now.') })
                                                    }
                                                }}
                                                startIcon={liveOpsActive ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                                            >
                                                {liveOpsActive ? t('Pause') : t('Start Polling')}
                                            </ToolbarButton>
                                        </Stack>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={5}>
                                                <OverviewStat
                                                    label={t('Status')}
                                                    value={botRunning ? t('Running') : t('Stopped')}
                                                    caption={`${t('Last Seen')}: ${formatDateTime(lastSeenAt)}`}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <OverviewStat
                                                    label={t('Log Polling')}
                                                    value={liveOpsActive ? t('Active') : t('Inactive')}
                                                    caption={liveOpsActive ? t('Polling Active') : t('Click to start')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        minHeight: 120,
                                                        borderRadius: 2.6,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                                        border: '1px solid rgba(255,255,255,0.06)'
                                                    }}
                                                >
                                                    <Switch checked={liveOpsActive} onChange={() => liveOpsActive ? onStopLiveOps?.() : onStartLiveOps?.()} />
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {t('Live Ops')}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Box
                                            sx={{
                                                minHeight: 220,
                                                borderRadius: 3,
                                                display: 'grid',
                                                placeItems: 'center',
                                                textAlign: 'center',
                                                backgroundColor: 'rgba(109, 126, 160, 0.18)',
                                                border: '1px solid rgba(255,255,255,0.07)'
                                            }}
                                        >
                                            <Stack spacing={1.2} alignItems="center">
                                                <TimelineRoundedIcon sx={{ fontSize: 34, color: 'rgba(255,255,255,0.65)' }} />
                                                <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                                                    {botRunning ? (liveOpsActive ? t('Live Ops Ready') : t('Bot is Running')) : t('Bot is Stopped')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: 'rgba(220,228,242,0.78)', maxWidth: 520 }}>
                                                    {botRunning ? (
                                                        liveOpsActive ? t('Start polling to view logs') : t('Live monitoring is available for this bot right now')
                                                    ) : t('Start your bot to begin monitoring and log polling')}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Paper>

                                <Paper
                                    sx={{
                                        p: 3,
                                        borderRadius: 3.2,
                                        backgroundColor: 'rgba(13,23,40,0.92)',
                                        border: '1px solid rgba(255,255,255,0.06)'
                                    }}
                                >
                                    <Stack spacing={2.1}>
                                        <Stack
                                            direction={{ xs: 'column', lg: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'stretch', lg: 'center' }}
                                            spacing={1.5}
                                        >
                                            <Stack direction="row" spacing={1.3} alignItems="center">
                                                <PublicRoundedIcon sx={{ color: 'primary.main' }} />
                                                <Typography variant="h5" sx={{ fontSize: '1.35rem' }}>
                                                    {t('Bot Logs')}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={liveOpsActive ? t('Polling Active') : t('Polling Inactive')}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.12)',
                                                        color: 'text.secondary',
                                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                                    }}
                                                />
                                            </Stack>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1}>
                                                <ToolbarButton startIcon={<PauseRoundedIcon />} onClick={() => onStopLiveOps?.()} disabled={!liveOpsActive}>
                                                    {t('Pause')}
                                                </ToolbarButton>
                                                <ToolbarButton startIcon={<CloudDownloadRoundedIcon />} onClick={handleExportLogs} disabled={!visibleLogText}>
                                                    {t('Export')}
                                                </ToolbarButton>
                                                <ToolbarButton
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<DeleteOutlineRoundedIcon />}
                                                    onClick={() => setLogsHidden(true)}
                                                >
                                                    {t('Clear')}
                                                </ToolbarButton>
                                            </Stack>
                                        </Stack>

                                        <Box
                                            ref={logViewportRef}
                                            sx={{
                                                minHeight: 620,
                                                maxHeight: 720,
                                                overflow: 'auto',
                                                borderRadius: 2.8,
                                                backgroundColor: '#040811',
                                                border: '1px solid rgba(255,255,255,0.07)',
                                                px: 2.25,
                                                py: 2,
                                                fontFamily: '"Cascadia Code", "SFMono-Regular", Consolas, monospace',
                                                fontSize: '0.95rem',
                                                lineHeight: 1.7,
                                                color: 'rgba(201, 213, 255, 0.8)',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {visibleLogText || t('No logs captured yet.')}
                                        </Box>

                                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ color: 'text.secondary' }}>
                                            <Typography variant="body2" sx={{ color: autoScroll ? '#10b981' : 'text.secondary', fontWeight: 700 }}>
                                                {`${t('Auto-scroll')}: ${autoScroll ? 'On' : 'Off'}`}
                                            </Typography>
                                            <Typography variant="body2">|</Typography>
                                            <Typography variant="body2">
                                                {`${t('Updates')}: ${liveOpsActive ? t('Active') : t('Stopped')}`}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => setAutoScroll(previous => !previous)}
                                                sx={{
                                                    ml: 'auto',
                                                    borderRadius: 2,
                                                    border: '1px solid rgba(255,255,255,0.08)',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                <TimelineRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Box>

                <BotTimetableDialog
                    open={timetableOpen}
                    value={timetable}
                    onClose={() => setTimetableOpen(false)}
                    onSave={nextTimetable => {
                        setTimetable(normalizeTimetable(nextTimetable))
                        setTimetableOpen(false)
                    }}
                    appLanguage={appLanguage}
                />
        </MainLayout>
    )
}
